import { ApolloError, DocumentNode, OperationVariables, QueryHookOptions } from '@apollo/client';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import { useDispatch } from 'react-redux';

import { queryTemplateAndProjectLocales } from 'api/queries';
import { Locale, WhatsAppActiveLocale } from 'modules/domains/template';
import { addNegativeToast } from 'modules/toastContainer';
import { useGraphql } from 'utils/hooks/useGraphql';
import { Nullable } from 'utils/tsHelpers';

export type TemplateAndProjectLocales = {
  templateId: number;
  isTemplateDefaultLocaleModifiable: boolean;
  templateDefaultLocale?: Nullable<Locale>;
  activeTemplateLocales: Locale[];
  allProjectLocales: Locale[];
  invalidWhatsAppLocales?: Locale[];
  validWhatsAppLocales?: Locale[];
  activeWhatsAppTemplateLocales?: WhatsAppActiveLocale[];
};

export type TemplateAndProjectLocalesResponse = {
  templateAndProjectLocales: TemplateAndProjectLocales;
};

type UseQueryTemplateAndProjectLocalesState = {
  errorMessage?: ApolloError;
  hasError: boolean;
  isLoading: boolean;
  refetch: () => void;
  templateAndProjectLocales: TemplateAndProjectLocales;
  /**
   * @deprecated use `templateAndProjectLocales` instead
   */
  results: TemplateAndProjectLocales;
};

const GET_LOCALES_ERROR =
  'Unable to fetch locales. Please try again, or contact your account manager.';

export const useQueryTemplateAndProjectLocales = (
  options: QueryHookOptions<TemplateAndProjectLocalesResponse, OperationVariables>,
  query: DocumentNode = queryTemplateAndProjectLocales,
): UseQueryTemplateAndProjectLocalesState => {
  const dispatch = useDispatch();
  const {
    data,
    isLoading,
    error: hasError = false,
    errorMessage,
    refetch,
  } = useGraphql(query, {
    notifyOnNetworkStatusChange: true,
    onError: () => {
      dispatch(addNegativeToast(GET_LOCALES_ERROR));
    },
    ...options,
  });

  /**
   * Adding Guard Against Duplicate Locales Being Created in Active Template Locales
   */
  const activeTemplateLocales = uniqBy(
    data?.templateAndProjectLocales?.activeTemplateLocales ?? [],
    'tag',
  );
  const activeWhatsAppTemplateLocales = uniqBy(
    data?.templateAndProjectLocales?.activeWhatsAppTemplateLocales ?? [],
    (approvableLocale) => approvableLocale?.locale?.tag,
  );

  const allMediumsActiveTemplateLocales =
    activeTemplateLocales?.length > 0
      ? activeTemplateLocales
      : activeWhatsAppTemplateLocales?.map((locale) => locale.locale);

  const templateAndProjectLocales = (
    isEmpty(data?.templateAndProjectLocales)
      ? (data?.templateAndProjectLocales ?? {})
      : {
          ...data?.templateAndProjectLocales,
          activeTemplateLocales: allMediumsActiveTemplateLocales,
          ...(activeWhatsAppTemplateLocales
            ? {
                activeWhatsAppTemplateLocales,
              }
            : {}),
        }
  ) as TemplateAndProjectLocales;

  return {
    errorMessage,
    hasError,
    isLoading,
    refetch,
    templateAndProjectLocales,
    results: templateAndProjectLocales,
  };
};
